import { useMemo, useState } from "react";
import { Check } from "lucide-react";
import { Text } from "@ferrivia/ui";
import { useFormatters, useMarket, useTranslation } from "@ferrivia/i18n";
import { useTracker } from "@ferrivia/auth-web";
import { NavLink } from "react-router";
import type { ClinicThreadSummary, JourneyStepState, PatientSummary } from "@ferrivia/api-contract";
import { useChatSummaries } from "../../hooks/useChatSummaries";
import { patientDisplayName, usePatients } from "../../hooks/usePatients";
import s from "./QueueList.module.css";

type Filter = "all" | "needs-reply" | "waiting-on-client" | "infusion";
type Sort = "newest" | "name" | "oldest-unanswered" | "reply-by";

function rowClass({ isActive }: { isActive: boolean }) {
  return isActive ? `${s.row} ${s.rowActive}` : s.row;
}

function lastMessageLabel(summary: ClinicThreadSummary | undefined, tag: string): string | null {
  if (!summary?.lastMessageAt) {
    return null;
  }
  return new Intl.DateTimeFormat(tag, { day: "numeric", month: "short" }).format(new Date(summary.lastMessageAt));
}

function dayLabel(iso: string, tag: string): string {
  return new Intl.DateTimeFormat(tag, { day: "numeric", month: "short" }).format(new Date(iso));
}

export function QueueList() {
  const { t } = useTranslation("clinic");
  const market = useMarket();
  const { tag, compare } = useFormatters();
  const tracker = useTracker();
  const [filter, setFilter] = useState<Filter>("all");
  const state = usePatients(filter === "infusion" ? "infusion" : undefined);
  const summariesState = useChatSummaries();
  const [sort, setSort] = useState<Sort>("newest");

  function stepStateLabel(stepState: JourneyStepState): string {
    if (stepState === "done") return t("queueList.stepDone", "done");
    if (stepState === "in_progress") return t("queueList.stepInProgress", "in progress");
    return t("queueList.stepTodo", "pending");
  }

  function stepIcon(stepState: JourneyStepState) {
    return stepState === "done" ? (
      <Check size={14} aria-hidden="true" />
    ) : (
      <span className={s.stepIconSpacer} aria-hidden="true" />
    );
  }

  function selectFilter(next: Filter) {
    setFilter(next);
    if (next === "waiting-on-client") {
      setSort("reply-by");
    }
    tracker.track("clinic.queue_filter_selected", { filter: next });
  }

  const summaryByPatientId = useMemo(() => {
    const map = new Map<string, ClinicThreadSummary>();
    if (summariesState.status === "success") {
      for (const summary of summariesState.summaries) {
        map.set(summary.patientId, summary);
      }
    }
    return map;
  }, [summariesState]);

  const unnamedPatient = t("queueList.unnamedPatient", "Unnamed patient");

  const visiblePatients = useMemo(() => {
    if (state.status !== "success") {
      return [];
    }
    let patients = state.patients;
    if (filter === "needs-reply") {
      patients = patients.filter((p) => summaryByPatientId.get(p.id)?.awaitingReplySince != null);
    }
    if (filter === "waiting-on-client") {
      patients = patients.filter((p) => summaryByPatientId.get(p.id)?.waitingOnClient != null);
    }
    const sorted = [...patients];
    if (sort === "reply-by") {
      sorted.sort((a, b) => {
        const aBy = summaryByPatientId.get(a.id)?.waitingOnClient?.replyBy;
        const bBy = summaryByPatientId.get(b.id)?.waitingOnClient?.replyBy;
        if (!aBy && !bBy) {
          return 0;
        }
        if (!aBy) {
          return 1;
        }
        if (!bBy) {
          return -1;
        }
        return new Date(aBy).getTime() - new Date(bBy).getTime();
      });
    } else if (sort === "name") {
      sorted.sort((a, b) => compare(patientDisplayName(a, unnamedPatient), patientDisplayName(b, unnamedPatient)));
    } else if (sort === "oldest-unanswered") {
      sorted.sort((a, b) => {
        const aSince = summaryByPatientId.get(a.id)?.awaitingReplySince;
        const bSince = summaryByPatientId.get(b.id)?.awaitingReplySince;
        if (!aSince && !bSince) {
          return 0;
        }
        if (!aSince) {
          return 1;
        }
        if (!bSince) {
          return -1;
        }
        return new Date(aSince).getTime() - new Date(bSince).getTime();
      });
    } else {
      sorted.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
    }
    return sorted;
  }, [state, filter, sort, summaryByPatientId, compare, unnamedPatient]);

  return (
    <section className={s.root} aria-label={t("queueList.regionLabel", "Queue")}>
      <header className={s.header}>
        <div className={s.titleRow}>
          <Text as="h1" size="xl" weight="bold" className={s.title}>
            {t("queueList.title", "Queue")}
          </Text>
          <Text as="span" size="sm" className={s.count}>
            {state.status === "success"
              ? t("queueList.patientCount", { count: state.patients.length, defaultValue: "{{count}} patients" })
              : ""}
          </Text>
        </div>
        <div className={s.chips}>
          <button
            type="button"
            className={filter === "all" ? `${s.chip} ${s.chipActive}` : s.chip}
            aria-pressed={filter === "all"}
            onClick={() => selectFilter("all")}
          >
            {t("queueList.filterAll", "All")}
          </button>
          <button
            type="button"
            className={filter === "needs-reply" ? `${s.chip} ${s.chipActive}` : s.chip}
            aria-pressed={filter === "needs-reply"}
            onClick={() => selectFilter("needs-reply")}
          >
            {t("queueList.filterNeedsReply", "Needs reply")}
          </button>
          <button
            type="button"
            className={filter === "waiting-on-client" ? `${s.chip} ${s.chipActive}` : s.chip}
            aria-pressed={filter === "waiting-on-client"}
            onClick={() => selectFilter("waiting-on-client")}
          >
            {t("queueList.filterWaitingOnClient", "Waiting on client")}
          </button>
          {market === "fi" && (
            <button
              type="button"
              className={filter === "infusion" ? `${s.chip} ${s.chipActive}` : s.chip}
              aria-pressed={filter === "infusion"}
              onClick={() => selectFilter("infusion")}
            >
              {t("queueList.filterInfusion", "Infusion")}
            </button>
          )}
          <button type="button" className={s.chip} disabled>
            {t("queueList.filterLabsPending", "Labs pending")}
          </button>
          <button type="button" className={s.chip} disabled>
            {t("queueList.filterNew", "New")}
          </button>
        </div>
        <div className={s.sortRow}>
          <label htmlFor="queue-sort" className={s.sortLabel}>
            {t("queueList.sortLabel", "Sort")}
          </label>
          <select
            id="queue-sort"
            className={s.sort}
            value={sort}
            onChange={(event) => setSort(event.target.value as Sort)}
          >
            <option value="oldest-unanswered">{t("queueList.sortOldestUnanswered", "Oldest unanswered")}</option>
            <option value="reply-by">{t("queueList.sortReplyBy", "Reply-by date")}</option>
            <option value="newest">{t("queueList.sortNewest", "Newest")}</option>
            <option value="name">{t("queueList.sortName", "Name")}</option>
            <option value="priority" disabled>
              {t("queueList.sortPriority", "Priority")}
            </option>
          </select>
        </div>
      </header>
      <div className={s.list}>
        {state.status === "loading" && (
          <Text as="p" size="sm" className={s.empty}>
            {t("queueList.loading", "Loading queue…")}
          </Text>
        )}
        {state.status === "error" && (
          <Text as="p" size="sm" className={s.empty}>
            {t("queueList.loadError", "Couldn't load the queue.")}
          </Text>
        )}
        {state.status === "success" && visiblePatients.length === 0 && (
          <Text as="p" size="sm" className={s.empty}>
            {filter === "waiting-on-client"
              ? t(
                  "queueList.emptyWaitingOnClient",
                  "No clients with a reply-by date. Set one when you schedule a message.",
                )
              : t("queueList.empty", "Nothing here yet.")}
          </Text>
        )}
        {state.status === "success" && visiblePatients.length > 0 && (
          <ul className={s.rows}>
            {visiblePatients.map((patient: PatientSummary) => {
              const summary = summaryByPatientId.get(patient.id);
              const lastMessage = lastMessageLabel(summary, tag);
              const waiting = filter === "waiting-on-client" ? summary?.waitingOnClient : null;
              return (
                <li key={patient.id}>
                  <NavLink to={`/queue/${patient.id}`} className={rowClass}>
                    <span className={s.rowTop}>
                      <span className={s.rowMain}>
                        <Text as="span" size="sm" className={s.rowName}>
                          {patientDisplayName(patient, unnamedPatient)}
                        </Text>
                        {summary && summary.unreadCount > 0 ? (
                          <span
                            className={s.unreadDot}
                            aria-label={t("queueList.unreadAriaLabel", {
                              count: summary.unreadCount,
                              defaultValue: "{{count}} unread messages",
                            })}
                          />
                        ) : null}
                      </span>
                      {lastMessage ? (
                        <Text as="span" size="xs" className={s.rowMeta}>
                          {lastMessage}
                        </Text>
                      ) : null}
                    </span>
                    {waiting ? (
                      <Text as="span" size="xs" className={s.rowMeta}>
                        {waiting.setByName
                          ? t("queueList.replyByLine", {
                              date: dayLabel(waiting.replyBy, tag),
                              name: waiting.setByName,
                              defaultValue: "Reply by {{date}} · set by {{name}}",
                            })
                          : t("queueList.replyByLineNoName", {
                              date: dayLabel(waiting.replyBy, tag),
                              defaultValue: "Reply by {{date}}",
                            })}
                        {waiting.nudgedAt
                          ? ` · ${t("queueList.nudgedOn", {
                              date: dayLabel(waiting.nudgedAt, tag),
                              defaultValue: "nudged {{date}}",
                            })}`
                          : ""}
                      </Text>
                    ) : null}
                    {patient.infusionSteps ? (
                      <Text as="span" size="xs" className={`${s.rowMeta} ${s.rowSteps}`}>
                        {stepIcon(patient.infusionSteps.labs)}
                        {t("queueList.stepLabs", "Labs")} {stepStateLabel(patient.infusionSteps.labs)}
                        {" · "}
                        {stepIcon(patient.infusionSteps.screener)}
                        {t("queueList.stepScreener", "Questionnaire")}{" "}
                        {stepStateLabel(patient.infusionSteps.screener)}
                      </Text>
                    ) : null}
                  </NavLink>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </section>
  );
}
